import '../locales/i18n';
import 'simplebar/src/simplebar.css';
import 'react-image-lightbox/style.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'react-quill/dist/quill.snow.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import 'react-lazy-load-image-component/src/effects/black-and-white.css';
import cookie from 'cookie';
import { ReactElement, ReactNode } from 'react';
import { NextPage } from 'next';
import Head from 'next/head';
import App, { AppProps, AppContext } from 'next/app';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/lib/integration/react';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { Amplify } from 'aws-amplify';
import { store, persistor } from '../redux/store';
import { getSettings } from '../utils/settings';
import { SettingsValueProps } from '../components/settings/type';
import { SettingsProvider } from '../contexts/SettingsContext';
import { CollapseDrawerProvider } from '../contexts/CollapseDrawerContext';
import ThemeProvider from '../theme';
import { ChartStyle } from '../components/chart';
import RtlLayout from '../components/RtlLayout';
import ProgressBar from '../components/ProgressBar';
import ThemeColorPresets from '../components/ThemeColorPresets';
import NotistackProvider from '../components/NotistackProvider';
import ThemeLocalization from '../components/ThemeLocalization';
import MotionLazyContainer from '../components/animate/MotionLazyContainer';
import { AuthProvider } from '../contexts/AmplifyAuthContext';
import * as Sentry from '@sentry/react';
import { awsconfig } from 'src/config';
import { ApolloProvider } from '@apollo/client';
import { client } from 'src/utils/apolloClient';
import ja from 'date-fns/locale/ja';
import { ErrorProvider } from 'src/contexts/ErrorContext';
import { ChannelIO } from '../modules/ChannelTalk';
import ProvideMSGraphContext from 'src/contexts/MSGraphContext';
import { BrowserRouter } from 'react-router-dom';
import { GoogleTagManager } from '@next/third-parties/google';
import Script from 'next/script';
import { CybozuAuthProvider } from '../contexts/CybozuAuthContext';

Amplify.configure({
  ...awsconfig,
  ssr: true,
});

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

interface MyAppProps extends AppProps {
  settings: SettingsValueProps;
  Component: NextPageWithLayout;
}

export default function MyApp(props: MyAppProps) {
  const { Component, pageProps, settings } = props;
  const getLayout = Component.getLayout ?? ((page) => page);

  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;

  return (
    <Sentry.ErrorBoundary>
      <Script
        id="user-guiding"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
                (function(g,u,i,d,e,s){
                  g[e]=g[e]||[];
                  var f=u.getElementsByTagName(i)[0];
                  var k=u.createElement(i);
                  k.async=true;
                  k.src='https://static.userguiding.com/media/user-guiding-'+s+'-embedded.js';
                  f.parentNode.insertBefore(k,f);
                  if(g[d]) return;
                  var ug=g[d]={q:[]};
                  ug.c=function(n){return function(){ug.q.push([n,arguments])};};
                  var m=['previewGuide','finishPreview','track','identify','hideChecklist','launchChecklist'];
                  for(var j=0;j<m.length;j++){ug[m[j]]=ug.c(m[j]);}
                })(window,document,'script','userGuiding','userGuidingLayer','KAX9863467ZID');
              `,
        }}
      />
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
      </Head>
      <GoogleTagManager gtmId={gtmId as string} />
      <AuthProvider>
        <ApolloProvider client={client}>
          <ReduxProvider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <CollapseDrawerProvider>
                <SettingsProvider defaultSettings={settings}>
                  <ThemeProvider>
                    <NotistackProvider>
                      <ErrorProvider>
                        <ProvideMSGraphContext>
                          <CybozuAuthProvider>
                            <MotionLazyContainer>
                              <ThemeColorPresets>
                                <ThemeLocalization>
                                  <LocalizationProvider
                                    dateAdapter={AdapterDateFns}
                                    dateFormats={{ normalDate: 'YYYY/mm/dd' }}
                                    locale={ja}
                                  >
                                    <RtlLayout>
                                      {/* <UserGuidingScripts /> */}
                                      <BrowserRouter>
                                        <ChartStyle />
                                        <ProgressBar />
                                        {getLayout(<Component {...pageProps} />)}
                                        <ChannelIO />
                                      </BrowserRouter>
                                    </RtlLayout>
                                  </LocalizationProvider>
                                </ThemeLocalization>
                              </ThemeColorPresets>
                            </MotionLazyContainer>
                          </CybozuAuthProvider>
                        </ProvideMSGraphContext>
                      </ErrorProvider>
                    </NotistackProvider>
                  </ThemeProvider>
                </SettingsProvider>
              </CollapseDrawerProvider>
            </PersistGate>
          </ReduxProvider>
        </ApolloProvider>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

MyApp.getInitialProps = async (context: AppContext) => {
  const appProps = await App.getInitialProps(context);

  const cookies = cookie.parse(
    context.ctx.req ? context.ctx.req.headers.cookie || '' : document.cookie
  );

  const settings = getSettings(cookies);

  return {
    ...appProps,
    settings,
  };
};
